import React, { useState, useEffect } from "react";
import { ConfigProvider } from "antd";
import "./landing.css";

import Navbar from "../../components/landing-page/Navbar";
import HeroSection from "../../components/landing-page/HeroSection";
import BenefitsSection from "../../components/landing-page/BenefitsSection";
import DualPerspectiveSection from "../../components/landing-page/DualPerspectiveSection";
import TestimonialsSection from "../../components/landing-page/TestimonialsSection";
import HowItWorksSection from "../../components/landing-page/HowItWorksSection";
import GetStartedCtaSection from "../../components/landing-page/GetStartedCtaSection";
import PricingHeroSection from "../../components/landing-page/PricingHeroSection";
import PricingSection from "../../components/landing-page/PricingSection";
import MobileShowcaseSection from "../../components/landing-page/MobileShowcaseSection";
import FaqSection from "../../components/landing-page/FaqSection";
import GrowWithSection from "../../components/landing-page/GrowWithSection";
import Footer from "../../components/landing-page/Footer";

import LoginModal from "../../components/landing-page/LoginModal";
import SignupModal from "../../components/landing-page/SignupModal";
import ForgotPwdModal from "../../components/landing-page/ForgotPwdModal";
import ResetPwdModal from "../../components/landing-page/ResetPwdModal";

function LandingPage({ onLoginSuccess }) {
  const [currentPage, setCurrentPage] = useState("home"); // "home" | "pricing"
  const [activeSection, setActiveSection] = useState("home");
  const [isNavbarScrolled, setIsNavbarScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Modal States
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const [isForgotPwdOpen, setIsForgotPwdOpen] = useState(false);
  const [isResetPwdOpen, setIsResetPwdOpen] = useState(false);
  const [isTrialRequest, setIsTrialRequest] = useState(false);
  const [resetToken, setResetToken] = useState("");

  // Check for resetToken in URL on mount
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("resetToken");
      if (token) {
        setResetToken(token);
        setIsResetPwdOpen(true);
      }
    } catch {
      // Ignore URL parsing error
    }
  }, []);

  // Scroll lock when any modal is open
  const isAnyModalOpen = isLoginOpen || isSignupOpen || isForgotPwdOpen || isResetPwdOpen;
  useEffect(() => {
    if (isAnyModalOpen) {
      document.body.classList.add("scroll-lock");
      document.documentElement.classList.add("scroll-lock");
    } else {
      document.body.classList.remove("scroll-lock");
      document.documentElement.classList.remove("scroll-lock");
    }
    return () => {
      document.body.classList.remove("scroll-lock");
      document.documentElement.classList.remove("scroll-lock");
    };
  }, [isAnyModalOpen]);

  // Navbar scroll & dynamic monotonic ScrollSpy listener
  useEffect(() => {
    const sections = [
      { id: "home", key: "home" },
      { id: "features", key: "features" },
      { id: "personas", key: "personas" },
      { id: "how-it-works", key: "how-it-works" },
      { id: "testimonials", key: "testimonials" },
      { id: "mobile-app", key: "mobile-app" },
      { id: "faq", key: "faq" },
    ];

    const handleScroll = () => {
      const scrollPos = window.scrollY;
      setIsNavbarScrolled(scrollPos > 30);

      if (currentPage === "pricing") {
        setActiveSection("pricing");
        return;
      }

      if (scrollPos < 100) {
        setActiveSection("home");
        return;
      }

      // Check if user has scrolled to the bottom of the page
      const isAtBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 80;
      if (isAtBottom) {
        setActiveSection("faq");
        return;
      }

      // Monotonic top-to-bottom detection with trigger offset
      const triggerPoint = Math.min(window.innerHeight * 0.35, 260);
      let detected = "home";

      for (const sec of sections) {
        const el = document.getElementById(sec.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= triggerPoint) {
            detected = sec.key;
          }
        }
      }

      setActiveSection(detected);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [currentPage]);

  const [initialSignupInput, setInitialSignupInput] = useState("");

  const closeModals = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(false);
    setIsForgotPwdOpen(false);
    setIsResetPwdOpen(false);
  };

  const openLogin = () => {
    closeModals();
    setIsLoginOpen(true);
  };

  const openSignup = (trial = false, initialVal = "") => {
    closeModals();
    setIsTrialRequest(trial);
    if (typeof initialVal === "string") {
      setInitialSignupInput(initialVal);
    }
    setIsSignupOpen(true);
  };

  const openTrialSignup = (initialVal = "") => {
    openSignup(true, initialVal);
  };

  const openForgotPwd = () => {
    closeModals();
    setIsForgotPwdOpen(true);
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const scrollTarget = () => {
      const el = document.getElementById(sectionId);
      if (el) {
        const navOffset = 70;
        const elementPosition = el.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - navOffset;
        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: "smooth",
        });
      }
    };

    if (currentPage !== "home") {
      setCurrentPage("home");
      setTimeout(scrollTarget, 120);
    } else {
      scrollTarget();
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#2459d3",
          borderRadius: 14,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
          colorTextBase: "#0f172a",
          colorTextSecondary: "#64748b",
        },
      }}
    >
      <div className="landing-wrapper">
        {/* NAVBAR */}
        <Navbar
          currentPage={currentPage}
          activeSection={currentPage === "pricing" ? "pricing" : activeSection}
          setPage={(page) => {
            setCurrentPage(page);
            setActiveSection(page);
          }}
          scrollToSection={scrollToSection}
          isNavbarScrolled={isNavbarScrolled}
          isMobileMenuOpen={isMobileMenuOpen}
          toggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          openLogin={openLogin}
          openSignup={() => openSignup(false)}
          openTrialSignup={openTrialSignup}
          isLoginOpen={isLoginOpen}
          isSignupOpen={isSignupOpen}
        />

        {/* HOME PAGE VIEW */}
        {currentPage === "home" && (
          <>
            <HeroSection openTrialSignup={openTrialSignup} openSignup={() => openSignup(false)} isLoginOpen={isLoginOpen} isSignupOpen={isSignupOpen} />
            <BenefitsSection currentPage={currentPage} />
            <DualPerspectiveSection openTrialSignup={openTrialSignup} />
            <HowItWorksSection openTrialSignup={openTrialSignup} />
            <TestimonialsSection />
            <GetStartedCtaSection
              openTrialSignup={openTrialSignup}
              openSignup={() => openSignup(true)}
            />
            <MobileShowcaseSection />
          </>
        )}

        {/* PRICING PAGE VIEW */}
        {currentPage === "pricing" && (
          <>
            <PricingHeroSection openSignup={() => openSignup(false)} openTrialSignup={openTrialSignup} />
            <PricingSection openSignup={() => openSignup(false)} openTrialSignup={openTrialSignup} />
            <BenefitsSection currentPage={currentPage} />
            <MobileShowcaseSection />
          </>
        )}

        {/* BLOCK 9: FAQ SECTION */}
        <FaqSection openTrialSignup={openTrialSignup} />

        {/* GROW WITH DEALVOICE SECTION */}
        <GrowWithSection />

        {/* FOOTER */}
        <Footer setPage={setCurrentPage} openTrialSignup={openTrialSignup} />

        {/* BOTTOM NAVIGATION */}

        {/* AUTH MODALS / DRAWERS */}
        <LoginModal isOpen={isLoginOpen} closeModals={closeModals} openSignup={openSignup} openForgotPwd={openForgotPwd} onLoginSuccess={onLoginSuccess} />

        <SignupModal isOpen={isSignupOpen} isTrialRequest={isTrialRequest} initialInput={initialSignupInput} closeModals={closeModals} openLogin={openLogin} />

        <ForgotPwdModal isOpen={isForgotPwdOpen} closeModals={closeModals} openLogin={openLogin} />

        <ResetPwdModal isOpen={isResetPwdOpen} token={resetToken} closeModals={closeModals} openLogin={openLogin} />
      </div>
    </ConfigProvider>
  );
}

export default LandingPage;
